import type { Season, SeasonType } from '@/types/season';

export const SEASON_TYPE_LABELS: Record<SeasonType, string> = {
  gun: 'Gun',
  bow: 'Bow',
  muzzleloader: 'ML',
  crossbow: 'XBow',
  youth: 'Youth',
  other: 'Other',
};

/**
 * Derives the Season Hero display chrome from a season (DASH-H2, issue #784).
 *
 * Returns two SEPARABLE pieces (no pre-composed single string) so DASH-TD1 can
 * place title and sub-label independently and never re-derive (wave-plan G4):
 *   - `title`   = `season.name` VERBATIM — no transform, uppercase, trim, or year-strip.
 *                 Season names are user-editable free text.
 *   - `subLabel`= `STATE · METHOD · YEAR` joined with " · " (space-middot-space),
 *                 where empty segments drop together with their separator (no dangling,
 *                 doubled, leading, or trailing `·`). In current types all three are
 *                 always present; the drop is defensive.
 *
 * METHOD reads `SEASON_TYPE_LABELS` READ-ONLY (the canonical method-label source shared with
 * the countdown subtitle and SeasonFormPage) — abbreviations stay verbatim (XBOW / ML),
 * never expanded, never mutated.
 */
export function deriveSeasonNameDisplay(season: Season): {
  title: string;
  subLabel: string;
} {
  const title = season.name;

  const state = season.state.toUpperCase();
  const method = SEASON_TYPE_LABELS[season.seasonType].toUpperCase();
  const year = season.openerDate.slice(0, 4);

  const subLabel = [state, method, year].filter(Boolean).join(' · ');

  return { title, subLabel };
}

/**
 * Returns true when a regulation `lastUpdated` date string (YYYY-MM-DD) is
 * more than 6 months before today.  Used to render a stale-data warning in
 * SeasonFormPage and testable without rendering the component.
 */
export function isRegulationDataStale(lastUpdated: string): boolean {
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  return new Date(lastUpdated) < sixMonthsAgo;
}
